import {MultiSelectionBox} from 'notebook/tables/constants/TableSelectionConstants';
/**
 * Computes whether fill-down is enabled for the table as a feature.
 * Fill-down is enabled when `enableFillDown` is explicitly true,
 * or when formulas are not disabled (fill-down is a formula-adjacent feature).
 *
 * @param enableFillDown - The explicit enableFillDown prop value
 * @param disableFormulas - Whether formulas are disabled for this table
 */
export function computeIsFillDownEnabled(
  enableFillDown: boolean | undefined,
  disableFormulas: boolean
): boolean {
  return enableFillDown === true || !disableFormulas;
}

/**
 * Computes whether fill-down is currently active (user is dragging the fill handle).
 * Reads from the multiExtra state and defaults to false when not set.
 *
 * @param fillDownActive - The fillDownActive state value from multiExtra
 */
export function computeIsFillDownActive(fillDownActive: boolean | undefined): boolean {
  return fillDownActive ?? false;
}

/**
 * Computes whether the current fill-down operation is invalid (e.g. dragging
 * over merged regions). Reads from the multiExtra state and defaults to false.
 *
 * @param fillDownInvalid - The fillDownInvalid state value from multiExtra
 */
export function computeIsFillDownInvalid(fillDownInvalid: boolean | undefined): boolean {
  return fillDownInvalid ?? false;
}

/**
 * Extracts the fill-down box from the multiExtra state.
 * The fill-down box represents the range of cells being filled.
 *
 * @param fillDownBox - The fillDownBox state value from multiExtra
 */
export function computeFillDownBox(
  fillDownBox: MultiSelectionBox | null | undefined
): MultiSelectionBox | null | undefined {
  return fillDownBox;
}

/**
 * Computes the renderable fill-down box by subtracting the portion that overlaps
 * with the current selection. Returns null when the fill-down box is fully
 * contained within the selection (nothing to render) or when inputs are missing.
 *
 * Handles both horizontal fills (same rows, extended columns) and vertical fills
 * (same columns, extended rows).
 *
 * @param fillDownBox - The raw fill-down box from state
 * @param expandedBox - The expanded selection box representing the current selection
 */
export function computeFillDownRenderBox(
  fillDownBox: MultiSelectionBox | null | undefined,
  expandedBox: MultiSelectionBox | null
): MultiSelectionBox | null {
  if (fillDownBox == null || expandedBox == null) {
    return null;
  }

  let {minI, maxI, minJ, maxJ} = fillDownBox;
  const {minI: sbMinI, maxI: sbMaxI, minJ: sbMinJ, maxJ: sbMaxJ} = expandedBox;

  // If the fill-down region is completely contained within the selection, don't render.
  if (sbMinI <= minI && minI <= maxI && maxI <= sbMaxI && sbMinJ <= minJ && minJ <= maxJ && maxJ <= sbMaxJ) {
    return null;
  }

  // Otherwise, subtract out the region that is in the selection.
  if (minI === sbMinI && maxI === sbMaxI) {
    // Horizontal fill
    if (maxJ > sbMaxJ) {
      minJ = sbMaxJ + 1;
    } else if (minJ < sbMinJ) {
      maxJ = sbMinJ - 1;
    }
  } else if (maxI > sbMaxI) {
    // Vertical fill extending down
    minI = sbMaxI + 1;
  } else if (minI < sbMinI) {
    // Vertical fill extending up
    maxI = sbMinI - 1;
  }

  return {minI, maxI, minJ, maxJ};
}
